import { type QueryClient, queryOptions } from '@tanstack/react-query';
import { navigation } from '@repo/api-kit/registration';
import { camelizeKeys } from '#utils/camelize-keys';
import { sessionStorage } from '#utils/session-storage';
import { companyQueries } from '#company/api';
import { customerQueries } from '#customer/api';
import { avatarQueries } from '#avatar/api';
import { featuresQueries } from '#features/api';
import { organizationCoreQueries } from '#organization-core/api';
import { kybQueries } from '#kyb/api';
import {
  registerBaseURL,
  registerNamespace,
  registerPartnersBaseURL,
  registerPartnersNamespace,
} from '../constants/hosts';
import { client } from '../utils/client/index';
import type {
  CompanyCreationOrganization,
  NavigationOrganization,
  PartnerOrganization,
  RegisteringOrganization,
} from './types';
import { adaptSearchOrganizationResponse } from './adapters/search-organization-adapter';
import type { DomainOrganizationSlices } from './adapters/graphql-to-domain-adapter';
import { searchOrganization } from './queries/search-organization';

export const organizationQueries = {
  all: ['organizations'] as const,
  detail: (organizationId: string) =>
    [...organizationQueries.all, 'detail', organizationId] as const,
  registering: () => [...organizationQueries.all, 'registering'] as const,
  companyCreation: () => [...organizationQueries.all, 'company-creation'] as const,
  partner: () => [...organizationQueries.all, 'partner'] as const,
};

/**
 * Returns query options for all navigation organizations.
 * Fetches from /navigation/v1/organizations endpoint.
 * `contract_signed_at` isn't covered by the proto yet, so it's read off the raw JSON.
 */
export function getAllOrganizationsQuery() {
  return queryOptions({
    queryKey: organizationQueries.all,
    queryFn: async () => {
      const json = await client<{ organizations: { contract_signed_at?: string | null }[] }>(
        '/navigation/v1/organizations'
      );
      const { organizations } =
        navigation.organization.firstparty.v1.ApiGetUserOrganizationsResponse.fromJSON(json);

      return organizations.map((organization, index): NavigationOrganization => ({
        ...organization,
        contractSignedAt: json.organizations[index]?.contract_signed_at ?? undefined,
      }));
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * Returns query options for registering organizations.
 * Cache is typically pre-populated by the Ember organization-manager service,
 * but will fetch from the API if the cache is not available.
 */
export function getRegisteringOrganizationsQuery() {
  return queryOptions({
    queryKey: organizationQueries.registering(),
    queryFn: async () => {
      const response = await client<{ organizations: RegisteringOrganization[] }>(
        `${registerBaseURL}/${registerNamespace}/organizations`,
        { method: 'GET' }
      );
      return camelizeKeys(response.organizations);
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * Returns company creation organizations from the register API.
 * Fetches from /register/v1/organizations and filters for non-FR orgs under company creation.
 * Cache is typically pre-populated by the Ember organization-manager service.
 */
export function getRegisterCompanyCreationOrganizationsQuery() {
  return queryOptions({
    queryKey: organizationQueries.companyCreation(),
    queryFn: async () => {
      const response = await client<{ organizations: RegisteringOrganization[] }>(
        `${registerBaseURL}/${registerNamespace}/organizations`,
        { method: 'GET' }
      );
      // Filter to only include non-FR organizations under company creation
      return response.organizations
        .filter(org => org.legalCountry !== 'FR' && org.underCompanyCreation)
        .map((org): CompanyCreationOrganization => ({
          id: org.id,
          name: org.name,
          slug: org.slug,
          legalCountry: org.legalCountry,
          underCompanyCreation: org.underCompanyCreation,
        }));
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * Returns query options for partner organizations.
 * Cache is typically pre-populated by the Ember organization-manager service,
 * but will fetch from the API if the cache is not available.
 *
 * Partner organizations are organizations with seamless registration flow and pending status.
 */
export function getPartnerOrganizationsQuery() {
  return queryOptions({
    queryKey: organizationQueries.partner(),
    queryFn: async () => {
      const params = new URLSearchParams({
        'filter[registration_status_eq]': 'pending',
        'filter[registration_flow_type_eq]': 'seamless',
      });
      const response = await client<{
        data: { id: string; attributes: { legal_name: string } }[];
      }>(
        `${registerPartnersBaseURL}/${registerPartnersNamespace}/organizations?${params.toString()}`,
        { method: 'GET' }
      );
      return response.data.map((org): PartnerOrganization => ({
        id: org.id,
        legalName: org.attributes.legal_name,
      }));
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * Returns the query options for fetching organization data via GraphQL.
 * Mirrors the behavior of the REST implementation on master, but uses GraphQL.
 *
 * @deprecated For new code, use the domain-typed hooks (useCurrentCompany, etc.)
 * or populateDomainOrganizationCaches for populating domain caches.
 *
 * Caching behavior:
 * - staleTime: Infinity = once data for this organizationId is cached, never refetch
 * - queryFn fetches via GraphQL and adapts the response
 */
export function getOrganizationQuery(organizationId: string) {
  return queryOptions({
    queryKey: organizationQueries.detail(organizationId),
    queryFn: async () => {
      if (!organizationId) {
        throw new Error('organizationId is required but was not provided');
      }

      // Set organization ID in sessionStorage for other components that may need it
      sessionStorage.setItem('organizationId', organizationId);

      // Legacy boundary: GraphQL organization payload is adapted to Ember shape
      // only for remaining legacy consumers of useCurrentOrganization.
      // Pass organizationId directly to ensure correct X-Qonto-Organization-Id header.
      const organizationGraphQLData = await searchOrganization({
        includeFinancialDetail: false,
        organizationId,
      });

      const adaptedData = adaptSearchOrganizationResponse(organizationGraphQLData);
      return camelizeKeys(adaptedData.organization);
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

// ============================================================================
// Domain orchestrator functions
// ============================================================================

/**
 * Populates all domain-typed caches from a GraphQL organization response.
 * This should be called by organization-manager after fetching organization data.
 *
 * Populates the following caches:
 * - company (from @repo/runtime-kit/company)
 * - customer (from @repo/runtime-kit/customer)
 * - avatar (from @repo/runtime-kit/avatar)
 * - features (from @repo/runtime-kit/features)
 * - organization core (from @repo/runtime-kit/organization-core)
 * - kyb (from @repo/runtime-kit/kyb)
 *
 * @param queryClient - The React Query client
 * @param organizationId - The organization ID
 * @param slices - The domain-typed organization slices from adaptGraphQLOrganizationToDomain
 */
export function populateDomainOrganizationCaches(
  queryClient: QueryClient,
  organizationId: string,
  slices: DomainOrganizationSlices
): void {
  queryClient.setQueryData(companyQueries.detail(organizationId).queryKey, slices.company);
  queryClient.setQueryData(customerQueries.detail(organizationId).queryKey, slices.customer);
  queryClient.setQueryData(avatarQueries.detail(organizationId).queryKey, slices.avatar);
  queryClient.setQueryData(featuresQueries.detail(organizationId).queryKey, slices.features);
  queryClient.setQueryData(
    organizationCoreQueries.detail(organizationId).queryKey,
    slices.organizationCore
  );
  queryClient.setQueryData(kybQueries.detail(organizationId).queryKey, slices.kyb);
}

export {
  adaptGraphQLOrganizationToDomain,
  adaptCompanyForFromJSON,
} from './adapters/graphql-to-domain-adapter';
